import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  HStack,
  IconButton,
  Link,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@ecoinvent/ui';
import { BiArrowToRight } from 'react-icons/bi';
import { FiExternalLink } from 'react-icons/fi';

import useFlowDetails from 'hooks/data/datasets/V4/flows/useFlowDetails';
import useFlows from 'hooks/data/datasets/V4/flows/useFlows';
import { SupplyChainNode } from 'hooks/data/datasets/V4/supplyChain/types';
import useVersionInfo from 'hooks/useVersionInfo';

import { COLORS } from './constants';

type Props = {
  node: SupplyChainNode | null;
  onClose: () => void;
};

type InfoRowProps = {
  label: string;
  value: string;
};

const InfoRow = ({ label, value }: InfoRowProps) => {
  const textColor = useColorModeValue('gray.800', 'gray.100');
  if (!value) return null;
  return (
    <Stack spacing={0.5}>
      <Text fontSize="md" fontWeight="bold" fontFamily="heading" color={textColor}>
        {label}
      </Text>
      <Text fontSize="md" color={textColor}>
        {value}
      </Text>
    </Stack>
  );
};

const FlowsDetailSection = ({ nodeId }: { nodeId: string }) => {
  const subtextColor = useColorModeValue('gray.800', 'gray.100');
  const { data: flows, isLoading: flowsLoading } = useFlows(nodeId);
  const referenceFlow = flows?.technosphere.outputs.find((f) => f.group === 'reference flow');
  const { data: flowDetails, isLoading: detailsLoading } = useFlowDetails(nodeId, 'outputs', referenceFlow?.id);

  const isLoading = flowsLoading || detailsLoading;

  if (isLoading) {
    return (
      <Stack align="center" py={4}>
        <Spinner size="sm" color="green.500" />
      </Stack>
    );
  }

  if (!flowDetails) {
    return (
      <Text fontSize="sm" color={subtextColor}>
        No flow details available.
      </Text>
    );
  }

  return (
    <Stack spacing={4}>
      {flowDetails.classification.CPC && <InfoRow label="CPC" value={flowDetails.classification.CPC.value} />}
      {flowDetails.classification.HS2017 && <InfoRow label="HS2017" value={flowDetails.classification.HS2017.value} />}
      {flowDetails.properties.map((prop) => (
        <Stack key={prop.name} spacing={0.5}>
          <Text fontSize="md" fontWeight="bold" fontFamily="heading" color={subtextColor}>
            {prop.name}
          </Text>
          <Text fontSize="md" color={subtextColor}>
            {prop.amount} {prop.unit}
          </Text>
        </Stack>
      ))}
    </Stack>
  );
};

const DatasetInfoModal = ({ node, onClose }: Props) => {
  const { ver, system_model } = useVersionInfo();
  const accordionBg = 'transparent';
  const linkColor = useColorModeValue(COLORS.expandGreenLight, COLORS.linkGreenDark);

  return (
    <Drawer isOpen={!!node} onClose={onClose} placement="right" size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader px={16} pt={16} pb={3}>
          <HStack spacing={3}>
            <IconButton
              variant="outline"
              colorScheme="gray"
              onClick={onClose}
              aria-label="Close"
              icon={<BiArrowToRight />}
              size="lg"
            />
            <Text fontSize="3xl" fontWeight="bold">
              Dataset Info
            </Text>
          </HStack>
        </DrawerHeader>

        <DrawerBody px={16} py={5}>
          {node && (
            <Stack spacing={5}>
              <Link
                href={`/${ver}/${system_model}/dataset/${node.id}/documentation`}
                isExternal
                color={linkColor}
                fontSize="md"
                fontWeight="bold"
                display="inline-flex"
                alignItems="center"
                gap="4px"
              >
                {node.name} <FiExternalLink size={14} />
              </Link>

              <Accordion key={node.id} allowMultiple defaultIndex={[0, 1]}>
                <AccordionItem border="none">
                  <AccordionButton bg={accordionBg} borderRadius="md" px={0} py={2.5} _hover={{ bg: accordionBg }}>
                    <Box flex={1} textAlign="left">
                      <Text fontWeight="bold" fontSize="xl" fontFamily="heading">
                        General Info
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel py={4} px={0}>
                    <Stack spacing={4}>
                      <InfoRow label="Geography" value={node.geography} />
                      <InfoRow label="Functional flow" value={node.functional_flow} />
                      <InfoRow label="Unit" value={node.unit} />
                      <InfoRow label="Time period" value={node.time_period} />
                      <InfoRow label="Activity type" value={node.type} />
                      {node.general_comment && <InfoRow label="General comment" value={node.general_comment} />}
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>

                <AccordionItem border="none" mt={2}>
                  <AccordionButton bg={accordionBg} borderRadius="md" px={0} py={2.5} _hover={{ bg: accordionBg }}>
                    <Box flex={1} textAlign="left">
                      <Text fontWeight="bold" fontSize="xl" fontFamily="heading">
                        Flows detail
                      </Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel py={4} px={0}>
                    <FlowsDetailSection nodeId={node.id} />
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Stack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default DatasetInfoModal;
